import React from 'react';
import {useEditing} from '@/state/EditingContext';
import {sectionRange} from '@/state/sectionEditing';
import { GRID } from '@/utils/constants';
import { editLoopRegion, loopLabel } from '@/state/loopSchedule';

export const Ruler = ({ totalBars, sectionMarkers = [], playheadBar = 0, loopRegion, loopEnabled, onClickBar, onLoopRegion, onToggleLoop, pxPerBar = GRID.PX_PER_BAR, kiosk = false, endBar = null, endLabel = null }) => {
  const editing=useEditing();
  const didRange=React.useRef(false);
  const rangeCleanup=React.useRef(null);
  React.useEffect(()=>()=>rangeCleanup.current?.(),[]);
  const startRange=e=>{
    if(e.button!==0||!editing.onSelectRange)return;
    rangeCleanup.current?.();
    const node=e.currentTarget,id=e.pointerId,x=e.clientX;let moved=false,range=null;
    const at=event=>Math.max(0,Math.min(totalBars,Math.round((event.clientX-node.getBoundingClientRect().left)/pxPerBar)));
    const anchor=at(e);node.setPointerCapture?.(id);
    const move=event=>{if(event.pointerId!==id||Math.abs(event.clientX-x)<4)return;moved=true;const to=at(event);range={startBar:Math.min(anchor,to),endBar:Math.max(anchor,to)};if(range.endBar>range.startBar)setRangeDraft(range);};
    const clean=()=>{window.removeEventListener('pointermove',move);window.removeEventListener('pointerup',up);window.removeEventListener('pointercancel',cancel);window.removeEventListener('keydown',key);rangeCleanup.current=null;setRangeDraft(null);if(node.hasPointerCapture?.(id))node.releasePointerCapture(id);};
    const up=event=>{if(event.pointerId!==id)return;move(event);clean();didRange.current=moved;if(range&&range.endBar>range.startBar)editing.onSelectRange(range);};
    const cancel=()=>{clean();didRange.current=true;};const key=event=>{if(event.key==='Escape')cancel();};
    window.addEventListener('pointermove',move);window.addEventListener('pointerup',up);window.addEventListener('pointercancel',cancel);window.addEventListener('keydown',key);rangeCleanup.current=clean;
  };
  const [rangeDraft,setRangeDraft]=React.useState(null);
  const selectedRange=rangeDraft||editing.range;
  const totalWidth = totalBars * pxPerBar;
  const [draft, setDraft] = React.useState(null); // live loop region while dragging
  const shown = draft || loopRegion;
  const handleWidth = kiosk ? 14 : 8;

  const handleClick = (e) => {
    if(didRange.current){didRange.current=false;return;}
    if (!onClickBar) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const bar = Math.max(0, Math.min((e.clientX - rect.left) / pxPerBar, totalBars));
    onClickBar(bar);
  };

  // Every loop edit (draw, resize, move) runs through one pointer drag on the loop strip.
  const startLoopDrag = (e, mode) => {
    if (e.button !== 0 || !onLoopRegion) return;
    e.preventDefault(); e.stopPropagation();
    const strip = e.currentTarget.closest('[data-testid="loop-strip"]');
    const barAt = (event) => (event.clientX - strip.getBoundingClientRect().left) / pxPerBar;
    const anchor = barAt(e);
    const base = loopRegion || { startBar: 0, endBar: 4 };
    let moved = false;
    const regionFor = (event) => editLoopRegion(base, mode, anchor, barAt(event), { fine: event.shiftKey && !kiosk, totalBars });
    const target = e.currentTarget;
    target.setPointerCapture?.(e.pointerId);
    target.onpointermove = (event) => {
      if (Math.abs(barAt(event) - anchor) * pxPerBar > 3) moved = true;
      if (moved) setDraft(regionFor(event));
    };
    target.onpointerup = (event) => {
      target.onpointermove = null; target.onpointerup = null; target.onpointercancel = null;
      setDraft(null);
      // A plain click on the loop bar turns looping on or off; a click on empty strip draws a 1 bar loop.
      if (!moved && mode === 'move') { onToggleLoop?.(); return; }
      onLoopRegion(regionFor(event), { enable: true });
    };
    target.onpointercancel = () => { target.onpointermove = null; target.onpointerup = null; setDraft(null); };
  };

  return (
    <div className="shrink-0 select-none" style={{ width: totalWidth }}>
      {/* Loop strip: only for the loop. Drag empty space to draw one; drag its ends or middle. */}
      <div
        data-testid="loop-strip"
        title="Drag here to set a loop"
        className="relative"
        onPointerDown={(e) => startLoopDrag(e, 'new')}
        style={{ height: GRID.LOOP_STRIP_HEIGHT, background: 'var(--bg)', borderBottom: '1px solid var(--border)', cursor: 'crosshair' }}
      >
        {shown && (
          <div
            data-testid="loop-region"
            title={loopEnabled ? 'Click to turn the loop off. Drag to move it.' : 'Click to turn the loop on. Drag to move it.'}
            onPointerDown={(e) => startLoopDrag(e, 'move')}
            className="absolute top-0 flex items-center justify-center"
            style={{
              left: shown.startBar * pxPerBar,
              width: (shown.endBar - shown.startBar) * pxPerBar,
              height: '100%',
              borderRadius: 4,
              background: loopEnabled ? 'var(--green)' : 'rgba(0, 212, 170, 0.22)',
              border: '1px solid var(--green)',
              cursor: 'grab',
              zIndex: 2,
            }}
          >
            <span style={{ fontSize: 9, fontWeight: 800, color: loopEnabled ? 'var(--bg)' : 'var(--green)', whiteSpace: 'nowrap', overflow: 'hidden', pointerEvents: 'none' }}>
              {loopLabel(shown)}
            </span>
            {['start', 'end'].map((edge) => (
              <div
                key={edge}
                data-testid={`loop-${edge}-handle`}
                title={edge === 'start' ? 'Drag to change where the loop starts' : 'Drag to change where the loop ends'}
                onPointerDown={(e) => startLoopDrag(e, edge)}
                className="absolute top-0 h-full"
                style={{ [edge === 'start' ? 'left' : 'right']: -2, width: handleWidth, cursor: 'ew-resize', zIndex: 3 }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Bar numbers: click to move the playhead. */}
      <div
        data-testid="ruler" title="Drag across measures to select a section; click to move the playhead"
        className="relative cursor-pointer"
        onClick={handleClick}
        onPointerDown={startRange}
        style={{
          height: GRID.RULER_HEIGHT - GRID.LOOP_STRIP_HEIGHT,
          width: totalWidth,
          background: 'var(--bg-card)',
          borderBottom: '1px solid var(--border)',
        }}
      >
        {selectedRange && <div className="ba-range-ruler" data-testid="selected-measures" style={{left:selectedRange.startBar*pxPerBar,width:(selectedRange.endBar-selectedRange.startBar)*pxPerBar}} />}
        {shown && loopEnabled && (
          <div data-testid="loop-shade" className="absolute top-0 pointer-events-none" style={{ left: shown.startBar * pxPerBar, width: (shown.endBar - shown.startBar) * pxPerBar, height: '100%', background: 'rgba(0, 212, 170, 0.10)' }} />
        )}
        {Array.from({ length: totalBars }, (_, i) => (
          <div
            key={i}
            className="absolute top-0 flex items-end pb-1"
            style={{
              left: i * pxPerBar,
              width: pxPerBar,
              height: '100%',
              borderLeft: `1px solid ${i % 4 === 0 ? 'var(--text-dim)' : 'var(--border)'}`,
            }}
          >
            <span style={{ fontSize: 10, fontWeight: 500, color: i % 4 === 0 ? 'var(--text-secondary)' : 'var(--text-dim)', paddingLeft: 4 }}>
              {i + 1}
            </span>
          </div>
        ))}

        {sectionMarkers.map((marker, idx) => (
          <div
            key={idx}
            data-testid={`section-marker-${marker.name.toLowerCase()}`}
            role="button" tabIndex={0} aria-label={`Select ${marker.name} section`} onPointerDown={e=>e.stopPropagation()} onClick={e=>{e.stopPropagation();editing.onSelectRange?.(sectionRange(marker,sectionMarkers,totalBars));}} onKeyDown={e=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();e.stopPropagation();editing.onSelectRange?.(sectionRange(marker,sectionMarkers,totalBars));}}}
            className="absolute top-0 flex items-center"
            style={{
              cursor:'pointer',
              left: marker.startBar * pxPerBar,
              height: 18, paddingLeft: 6, paddingRight: 8,
              borderRadius: '0 0 8px 0',
              background: `${marker.color}22`,
              borderBottom: `2px solid ${marker.color}`,
            }}
          >
            <span style={{ fontSize: 9, fontWeight: 600, color: marker.color }}>{marker.name}</span>
          </div>
        ))}

        {Number.isFinite(endBar) && <div data-testid="class-end-line" className="class-end-line" style={{ left: endBar * pxPerBar }}><span>{endLabel}</span></div>}

        {/* Playhead — NO CSS transition, position set directly by RAF */}
        <div
          data-testid="playhead-ruler"
          className="absolute top-0 pointer-events-none"
          style={{ left: playheadBar * pxPerBar, width: 2, height: '100%', background: 'var(--green)', zIndex: 10 }}
        >
          <div style={{ width: 8, height: 8, background: 'var(--green)', borderRadius: '50%', position: 'absolute', top: -2, left: -3 }} />
        </div>
      </div>
    </div>
  );
};

export default Ruler;
